import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { LoginContext } from "context/Auth/LoginContext";
import usePost from "Hooks/usePost";
import useFetch from "Hooks/useFetch";
import useDelete from "Hooks/useDelete";
import usePut from "Hooks/usePut";
export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({
    books: [],
    courses: [],
    status: "active",
  });
  const { token, isLoggedIn, isVerified } = useContext(LoginContext);
  // Use a ref to track the latest token value without causing re-renders
  const tokenRef = useRef(token);
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingItems, setLoadingItems] = useState({});
  const [cartStatus, setCartStatus] = useState("active");
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  const { postData, isLoading, error } = usePost();
  const { fetchData: getCart, isLoading: isCartLoading } = useFetch();
  const { putData, isLoading: isPutLoading } = usePut();
  const {
    deleteData,
    isLoading: isDeleteLoading,
    error: deleteError,
  } = useDelete();

  const addToCart = async (item, tokenParam) => {
    // Check if cart is pending - use both cart?.status and cartStatus as fallback
    if (cart?.status === "pending" || cartStatus === "pending") {
      setSuccessMessage(
        "Your order is being processed. You cannot modify your cart."
      );
      setTimeout(() => setSuccessMessage(""), 3000);
      return;
    }

    const itemKey = `${item.type}-${item.slug}`;
    setLoadingItems((prev) => ({ ...prev, [itemKey]: true }));

    try {
      // Use provided token parameter or fall back to token ref
      const currentToken = tokenParam || tokenRef.current;

      // Ensure all required fields are present
      console.log("Adding to cart:", item);
      console.log("Required fields check:", {
        type: !!item.type,
        slug: !!item.slug,
        title: !!item.title,
        price: !!item.price,
        info: !!item.info,
      });

      const response = await postData("cart/add", item, currentToken);
      console.log("Cart add response:", response);

      setLoadingItems((prev) => ({ ...prev, [itemKey]: false }));

      // Handle authentication errors
      if (response && !response.success && response.status === 401) {
        setIsAuthenticated(false);
        console.error("Authentication error when adding to cart");
        return;
      }

      // Handle success
      if (response && response.success) {
        setIsAuthenticated(true);
        // Ensure we have valid data before updating state
        if (response.data) {
          console.log("Setting cart data:", response.data);
          setCart(response.data);
          setCartStatus(response.data.status || "active");
          setSuccessMessage("Item added to cart successfully");
          setTimeout(() => setSuccessMessage(""), 3000);
        } else {
          console.error("Response success but no data returned");
          // Fetch cart again to ensure we have latest data
          fetchCart();
          setSuccessMessage("Item added to cart successfully");
          setTimeout(() => setSuccessMessage(""), 3000);
        }
      } else {
        // Handle error response
        const errorMessage = response?.message || "Unknown error";
        const errorDetail = response?.error || "";
        console.error(
          `Failed to add item to cart: ${errorMessage}`,
          errorDetail
        );

        // Show detailed error to help debugging
        if (errorDetail) {
          setSuccessMessage(`Error: ${errorMessage}. Details: ${errorDetail}`);
        } else {
          setSuccessMessage(`Error adding item to cart: ${errorMessage}`);
        }
        setTimeout(() => setSuccessMessage(""), 5000);
      }
    } catch (err) {
      console.error("Error in addToCart:", err);
      setLoadingItems((prev) => ({ ...prev, [itemKey]: false }));
      setSuccessMessage("An error occurred. Please try again.");
      setTimeout(() => setSuccessMessage(""), 5000);
    }
  };

  // Update the fetchCart function to properly handle empty or invalid responses:
  const fetchCart = useCallback(async () => {
    console.log("Fetching cart with token:", tokenRef.current);
    // Use the token from ref to avoid dependency issues

    try {
      console.log("Fetching cart with token");
      const response = await getCart("cart", token);

      if (response && response.success && response.data) {
        setIsAuthenticated(true);
        // Ensure we have the correct structure even if some props are missing
        const cartData = {
          books: response.data.books || [],
          courses: response.data.courses || [],
          status: response.data.status || "active",
          total: response.data.total || 0,
        };
        setCart(cartData);
        setCartStatus(cartData.status);
      } else {
        console.log("Cart fetch failed or returned no data, using defaults");
        // Set a default empty cart structure
        setCart({ books: [], courses: [], status: "active", total: 0 });
        setCartStatus("active");
      }
    } catch (err) {
      console.error("Error fetching cart:", err);
      // Set a default empty cart structure on error
      setCart({ books: [], courses: [], status: "active", total: 0 });
      setCartStatus("active");
    }
  }, [getCart]);

  const updateCartStatus = async (status) => {
    if (token) {
      try {
        console.log("Calling updateCartStatus with:", status);
        const response = await postData("cart/status", { status }, token);
        console.log("Update cart status response:", response);

        if (response && response.success) {
          setCart(response.data);
          setCartStatus(response.data?.status || "active");
          return true;
        } else {
          const errorMsg = response
            ? response.message
            : "Failed to update cart status";
          console.error("Cart status update failed:", errorMsg);
          return false;
        }
      } catch (error) {
        console.error("Error in updateCartStatus:", error);
        return false;
      }
    }
    console.error("No token available for cart status update");
    return false;
  };

  const isItemInCart = (type, slug) => {
    // Make sure cart and cart[types] exist before checking
    if (!cart || !cart[`${type}s`] || !Array.isArray(cart[`${type}s`]))
      return false;
    return cart[`${type}s`].some((item) => item && item.slug === slug);
  };

  const isItemLoading = (type, slug) => {
    const itemKey = `${type}-${slug}`;
    return !!loadingItems[itemKey];
  };

  const removeFromCart = async (type, slug) => {
    // Check if cart is pending
    if (cart?.status === "pending") {
      setSuccessMessage(
        "Your order is being processed. You cannot modify your cart."
      );
      setTimeout(() => setSuccessMessage(""), 3000);
      return;
    }

    const itemKey = `${type}-${slug}`;
    setLoadingItems((prev) => ({ ...prev, [itemKey]: true }));

    try {
      console.log(`Removing from cart: ${type}/${slug}`);
      // Use the token from ref
      const response = await deleteData(
        `cart/remove/${type}/${slug}`,
        tokenRef.current
      );
      console.log("Remove response:", response);

      setLoadingItems((prev) => ({ ...prev, [itemKey]: false }));

      if (response && response.success) {
        if (response.data) {
          setCart(response.data);
          setCartStatus(response.data.status || "active");
        } else {
          console.log("Response success but no data, refreshing cart");
          fetchCart();
        }
      } else {
        console.error(
          "Failed to remove item from cart:",
          response?.message || "Unknown error"
        );
      }
    } catch (err) {
      console.error("Error in removeFromCart:", err);
      setLoadingItems((prev) => ({ ...prev, [itemKey]: false }));
    }
  };

  // Fetch cart when login state changes
  useEffect(() => {
    if (isLoggedIn && isVerified) {
      fetchCart();
    }
  }, [isLoggedIn, isVerified]);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        isLoading,
        error,
        fetchCart,
        isCartLoading,
        isItemInCart,
        isItemLoading,
        successMessage,
        setCart,
        removeFromCart,
        isDeleteLoading,
        deleteError,
        updateCartStatus,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
